import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Star } from 'lucide-react';
import { VIDEO_TESTIMONIALS } from '@/data/mockData';
import { SectionTitle } from '@/components/common/SectionTitle';
import { HOME } from '@/constants/testIds';

const isYouTubeUrl = (url) => /(?:youtube\.com|youtu\.be)/i.test(String(url || ''));
const isBunnyUrl = (url) => /(?:mediadelivery\.net|bunnycdn\.com)/i.test(String(url || ''));

const toEmbedUrl = (url) => {
  if (!url) return '';

  if (isYouTubeUrl(url)) {
    try {
      const parsed = new URL(url);
      const videoId = parsed.searchParams.get('v') || parsed.pathname.split('/').filter(Boolean).pop();
      if (!videoId) return url;
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    } catch (error) {
      return url;
    }
  }

  if (isBunnyUrl(url) && /\/play\//i.test(String(url))) {
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (segments.length >= 3) {
        const libraryId = segments[1];
        const videoId = segments[2];
        return `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}?autoplay=true&muted=true`;
      }
    } catch (error) {
      return url;
    }
  }

  return url;
};

const VideoTestimonials = () => {
  const [active, setActive] = useState(0);
  const [shouldLoadEmbed, setShouldLoadEmbed] = useState(false);
  const total = VIDEO_TESTIMONIALS.length;
  const prev = () => setActive((a) => (a - 1 + total) % total);
  const next = () => setActive((a) => (a + 1) % total);
  const current = VIDEO_TESTIMONIALS[active];
  const currentVideoUrl = toEmbedUrl(current?.video);
  const currentIsEmbed = isYouTubeUrl(current?.video) || isBunnyUrl(current?.video);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      try { videoRef.current.currentTime = 0; } catch (e) {}
      setIsPlaying(false);
    }
    setShouldLoadEmbed(false);
  }, [active]);

  return (
    <section className="relative py-24 sm:py-32 bg-[#050A14]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Student Voices"
          title={<span className="font-semibold">Real careers. Real transformations.</span>}
          subtitle="Hear from professionals who switched from manual testing to GenAI automation roles with BYMAT."
        />

        <div data-testid={HOME.videoTestimonialsCarousel} className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          {/* Featured */}
          <div className="lg:col-span-3">
            <div className="relative aspect-video rounded-2xl overflow-hidden glass border-brand-cyan/30 group">
              {current?.thumb && !shouldLoadEmbed && (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${current.thumb})`, zIndex: 0 }}
                />
              )}

              {currentIsEmbed && shouldLoadEmbed ? (
                <iframe
                  src={currentVideoUrl}
                  title={`${current.name} testimonial`}
                  className="absolute inset-0 h-full w-full"
                  style={{ zIndex: 1 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  onLoad={() => setIsPlaying(true)}
                />
              ) : !currentIsEmbed ? (
                <video
                  ref={videoRef}
                  src={current.video}
                  poster={current.thumb}
                  preload="metadata"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition"
                />
              ) : null}

              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

              {((!currentIsEmbed && !isPlaying) || (currentIsEmbed && !shouldLoadEmbed)) && (
                <button
                  type="button"
                  onClick={() => {
                    if (currentIsEmbed) {
                      setShouldLoadEmbed(true);
                      setIsPlaying(true);
                      return;
                    }
                    if (!videoRef) return;
                    const v = videoRef.current;
                    if (!v) return;
                    if (v.paused) v.play(); else v.pause();
                  }}
                  className="absolute inset-0 flex items-center justify-center z-10"
                  aria-label="Play"
                >
                  <span className="h-16 w-16 rounded-full bg-brand-cyan text-black flex items-center justify-center btn-glow pulse-ring">
                    <Play className="w-7 h-7 ml-1" fill="currentColor" />
                  </span>
                </button>
              )}

              <div className="absolute bottom-5 left-5 right-5 lg:left-7 lg:right-7">
                <div className="flex items-center gap-1 text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5" fill="currentColor" />)}
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold">{current.name}</h3>
                <p className="text-xs text-brand-cyan font-mono tracking-wider uppercase">{current.role}</p>
                <p className="mt-2 text-sm text-white/80 max-w-xl leading-relaxed">&ldquo;{current.quote}&rdquo;</p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs font-mono text-white/50">{String(active + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</div>
              <div className="flex gap-2">
                <button type="button" onClick={prev} aria-label="Previous" className="h-10 w-10 glass rounded-lg flex items-center justify-center hover:bg-brand-cyan/10 hover:text-brand-cyan transition">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button type="button" onClick={next} aria-label="Next" className="h-10 w-10 glass rounded-lg flex items-center justify-center hover:bg-brand-cyan/10 hover:text-brand-cyan transition">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-1 gap-3">
            {VIDEO_TESTIMONIALS.map((v, i) => (
              <button
                type="button"
                key={v.id}
                onClick={() => setActive(i)}
                className={`relative text-left rounded-xl overflow-hidden glass transition group ${
                  i === active ? 'border-brand-cyan/60 ring-1 ring-brand-cyan/30' : 'hover:border-brand-cyan/30'
                }`}
              >
                <div className="flex items-center gap-3 p-3">
                  <div className="relative h-16 w-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={v.thumb} alt={v.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="w-4 h-4 text-brand-cyan" fill="currentColor" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{v.name}</div>
                    <div className="text-[10px] text-brand-cyan/80 font-mono uppercase tracking-wider truncate">{v.role}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoTestimonials;
